<template>
  <div
    v-if="isVisible"
    class="sidebar"
    v-click-outside="clickOutside"
    :class="[activeComponent ? 'open' : '', position]"
  >
    <div class="sidebar-fix" :class="[position, sidebarClass]">
      <div class="sidebar-top-items main-menu-badge">
        <div v-for="(item, index) in activeItems.top" @click="clickOpen(item)" role="button" :key="item.name">
          <el-badge
            :hidden="hiddenBadge(item)"
            :value="getBadgeValue(item.name)"
            type="success"
            :max="getBadgeMaxValue(item.name)"
          >
            <span
              :class="itemClassByType(item)"
              :key="`top-${index}`"
              :helpy="item.helpySelector"
              :title="$i18n.t(item.title)"
            >
              {{ itemOutputByType(item) }}
            </span>
          </el-badge>
        </div>
      </div>
      <div class="sidebar-bottom-items">
        <template v-for="(item, index) in activeItems.bottom">
          <component
            v-if="item.component"
            :key="`bottom-${index}`"
            :is="item.component"
            :helpy="item.helpySelector"
            :title="$i18n.t(item.title)"
          />
          <span
            v-else
            :key="`bottom-${index}`"
            :class="itemClassByType(item)"
            :helpy="item.helpySelector"
            :title="$i18n.t(item.title)"
            @click="clickOpen(item)"
            role="button"
          >
            {{ itemOutputByType(item) }}
          </span>
        </template>
      </div>
    </div>
    <div :class="[activeComponent ? 'open' : '', position, sidebarClass]" class="sidebar-content">
      <component
        :is="activeComponent"
        v-bind="activeComponentProps || {}"
        @info-count="onInfoCount"
        @get-message-count="getMessageCount"
        @get-workflow-count="getWorkflowTaskCount"
      />
    </div>
  </div>
  <div v-else class="sidebar-collapse-button" :class="[position, sidebarClass]">
    <span @click="toggleSidebar" class="material-icons">expand_less</span>
  </div>
</template>

<script>
/**
 * @class SideBar
 *
 * @description sidebar component for ui
 */

import { get, find } from 'lodash'
import frameworkTheme from '../config-manager/frameworkTheme'
import FormLock from '../form/controls/FormLock'
import TopBar from './TopBar'
import SideBar from './SideBar'
import DropdownMenu from './DropdownMenu'
import Workflow from './Workflow'
import Bookmark from './Bookmark'
import SubsystemInfo from './SubsystemInfo'
import GlobalSearchMenu from '../global-search/GlobalSearchMenu'
import Screenshot from './Screenshot'
import InternalMessages from './InternalMessages'
import VoiceControl from '../voice-control/VoiceControl'
import OpenAiChat from '../openai-completion/OpenAiChat.vue'
import { items } from './utils/sidebarItems'

const WF_STATUS_ASSIGNED = 1
const WF_STATUS_INPROGRESS = 2

const WF_MAX_BADGE_COUNT = 10
const MSG_MAX_BADGE_COUNT = 99
const INFO_MAX_BADGE_COUNT = 99

const FUNCTIONS = {
  WORKFLOW: 'workflow',
  MESSAGE: 'message',
  MAIN_MENU: 'mainMenu',
  MENU_SEARCH: 'menuSearch',
  MENU_POSITION: 'menuPosition',
}

export default {
  name: 'SideBar',
  mixins: [frameworkTheme],
  components: {
    SideBar,
    TopBar,
    FormLock,
    DropdownMenu,
    Workflow,
    Bookmark,
    SubsystemInfo,
    GlobalSearchMenu,
    VoiceControl,
    Screenshot,
    OpenAiChat,
    InternalMessages,
  },
  data() {
    return {
      position: 'left',
      items: [],
      activeComponent: '',
      activeComponentProps: {},
      isVisible: true,
      infoCount: 0,
      body: document.getElementsByTagName('body')[0],
      sidebarClass: this.$CORE.subSystem.id === 26 ? 'int' : '',
      messageCount: 0,
      workflowCount: 0,
      idSUser: get(this, '$CORE.params.ID_S_USER', null),
      FUNCTIONS,
    }
  },
  computed: {
    activeItems() {
      return {
        top: items.filter(
          (item) => item.active && item.position === 'top' && this.enabledFunctions.includes(item.name)
        ),
        bottom: items.filter(
          (item) => item.active && item.position === 'bottom' && this.enabledFunctions.includes(item.name)
        ),
      }
    },
    enabledFunctions() {
      return this.$store.getters.enabledFunctions || []
    },
  },
  methods: {
    find,
    get,
    hiddenBadge(item) {
      let ret = true
      switch (item.name) {
        case this.FUNCTIONS.MESSAGE: {
          ret = !(item.badge && this.messageCount)
          break
        }
        case this.FUNCTIONS.WORKFLOW: {
          ret = !(item.badge && this.workflowCount)
          break
        }
        case this.FUNCTIONS.MAIN_MENU: {
          ret = !(item.badge && this.infoCount)
          break
        }
      }
      return ret
    },
    getBadgeValue(name) {
      switch (name) {
        case this.FUNCTIONS.WORKFLOW:
          return this.workflowCount
        case this.FUNCTIONS.MESSAGE:
          return this.messageCount
        default:
          return this.infoCount
      }
    },
    getBadgeMaxValue(name) {
      switch (name) {
        case this.FUNCTIONS.WORKFLOW:
          return WF_MAX_BADGE_COUNT
        case this.FUNCTIONS.MESSAGE:
          return MSG_MAX_BADGE_COUNT
        default:
          return INFO_MAX_BADGE_COUNT
      }
    },
    onInfoCount(value) {
      this.infoCount = value
    },
    getMenuInfo() {
      this.infoCount = this.$CORE.menuInfo
    },
    itemOutputByType(item) {
      return item.iconName || item.title
    },
    itemClassByType(item) {
      if (item.iconName) {
        if (item.name === this.FUNCTIONS.MENU_POSITION) {
          return item.iconClass + ' ' + this.position
        }
        return item.iconClass
      } else if (item.title) {
        return 'title'
      }
    },
    toggleSidebar() {
      this.isVisible = !this.isVisible
      if (this.isVisible) {
        this.setSidebarPosition()
      } else {
        this.body.classList.remove('sidebar-right')
        this.body.classList.remove('sidebar-left')
      }
      localStorage.setItem('isSidebarOpen', this.isVisible)
    },
    setSidebarPosition() {
      if (this.position === 'left') {
        this.body.classList.add('sidebar-left')
        this.body.classList.remove('sidebar-right')
      } else {
        this.body.classList.remove('sidebar-left')
        this.body.classList.add('sidebar-right')
      }
      localStorage.setItem('sidebarPosition', this.position)
      this.$store.commit('SET_SIDEBAR_POSITION', this.position)
    },
    clickOutside() {
      this.activeComponent = ''
    },
    clickOpen(item) {
      if (!item.component) {
        if (item.action === 'left-right-sidebar') {
          this.position = this.position === 'left' ? 'right' : 'left'
          this.setSidebarPosition()
        } else if (item.action === 'collapse-sidebar') {
          this.toggleSidebar()
        }
      } else {
        this.activeComponentProps = item.componentProps
        if (item.name === this.FUNCTIONS.MAIN_MENU) {
          this.activeComponentProps.searchable = this.enabledFunctions.includes(this.FUNCTIONS.MENU_SEARCH)
        }
      }

      this.activeComponent = this.activeComponent === item.component ? '' : item.component
    },
    async getMessageCount() {
      if (!this.enabledFunctions.includes(this.FUNCTIONS.MESSAGE)) return
      if (!this.idSUser) return
      this.messageCount = this.$CORE.messages
    },
    async getWorkflowTaskCount() {
      if (!this.enabledFunctions.includes(this.FUNCTIONS.WORKFLOW)) return
      if (!this.idSUser) return
      let assignedWfCount = this.$CORE.workflow[WF_STATUS_ASSIGNED]
      let inProgressWfCount = this.$CORE.workflow[WF_STATUS_INPROGRESS]
      this.workflowCount = assignedWfCount + inProgressWfCount
    },
  },
  watch: {
    enabledFunctions() {
      this.getMessageCount()
      this.getWorkflowTaskCount()
    },
  },
  created() {
    this.position = localStorage.getItem('sidebarPosition') || 'left'
    this.isVisible = localStorage.getItem('isSidebarOpen') || true
    this.setSidebarPosition()
    this.loadTheme()
    this.getMenuInfo()
  },
}
</script>

<style lang="scss" scoped>
.sidebar {
  z-index: 1990;
  display: flex;
  position: fixed;
  height: 100%;
  &.open {
    position: fixed;
    width: 400px;
  }
  &.left {
    left: 0;
  }
  &.right {
    right: 0;
  }
}
.sidebar-fix {
  position: fixed;
  z-index: 1995;
  padding-top: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: $sidebar-collapsed-width;
  height: 100%;
  background: $sidebar-color;
  &.int {
    background: $int-sidebar-color;
  }
  &.left {
    left: 0;
    > div span {
      color: $icon-large;
      padding: 10px 0px;
      font-size: 22px;
      transition: 0.5s;
      &:hover {
        color: white;
        cursor: pointer;
      }
      &.title {
        font-size: 12px;
        text-align: center;
      }
    }
  }
  &.right {
    right: 0;
    > div span {
      color: $icon-large;
      padding: 10px 0px;
      font-size: 22px;
      transition: 0.5s;
      &:hover {
        color: white;
        cursor: pointer;
      }
      &.title {
        font-size: 12px;
        text-align: center;
      }
    }
  }
}
.sidebar-bottom-items {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 10px;
}
.sidebar-top-items {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
}
.sidebar-content {
  padding-left: 10px;
  padding-top: 75px;
  z-index: 1993;
  width: 320px;
  height: 100%;
  background: $sidebar-color;
  position: fixed;
  transition: 0.5s;
  &.int {
    background: $int-sidebar-color;
  }
  &.left {
    left: -260px;
    &.open {
      position: fixed;
      left: 60px;
    }
  }
  &.right {
    right: -260px;
    &.open {
      position: fixed;
      right: 60px;
    }
  }
}
.material-icons {
  &.right {
    transform: rotate(180deg);
  }
  &.left {
    transform: rotate(0deg);
  }
}
.sidebar-collapse-button {
  cursor: pointer;
  z-index: 1993;
  background: $sidebar-color;
  color: $white;
  border-radius: 50%;
  position: fixed;
  bottom: 12px;
  padding: 5px;
  &.int {
    background: $int-sidebar-color;
  }
  &.left {
    left: 13px;
  }
  &.right {
    right: 13px;
  }
}
</style>
<style>
.main-menu-badge .el-badge sup.el-badge__content.is-fixed {
  top: 1px !important;
}
</style>
